import { usePiPWindow } from 'components/PiP/PiPProvider';
import { SourceDetails } from 'features/layout/types';
import { Box, IconButtonProps, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MicControlRemote } from 'features/streaming/components/controls/MicControlRemote';
import { MicControlLocal } from 'features/streaming/components/controls/MicControlLocal';
import { TileControls } from 'features/streaming/components/TileControls';
import { useAppSelector } from 'store/hooks';
import { selectIsBottomTileLayout } from 'features/layout/features/config/configSlice';
import TileLocalMuteIndicator from 'features/local-mute/TileLocalMuteIndicator';
import { useIsLocallyMuted } from 'features/local-mute/useIsLocallyMuted';
import React from 'react';
import { tileChromeClassName } from 'features/streaming/components/stream-display/tileChrome';

interface StreamControlsProps {
  source: SourceDetails;
}

export type StreamControlProps = IconButtonProps<
  'button',
  { source: SourceDetails; onClick?: (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => void }
>;

const Root = styled('div', {
  shouldForwardProp: (prop) => prop !== 'bottom',
})<{ bottom?: boolean }>(({ bottom }) => ({
  position: 'absolute',
  zIndex: 1,
  display: 'flex',
  ...(bottom ? { top: '8px', right: '8px' } : { bottom: '8px', left: '8px' }),
}));

export const TileControlButtons = React.memo(({ source }: StreamControlsProps) => {
  const local = source.kind === 'local';
  const bottom = useAppSelector(selectIsBottomTileLayout);
  const isLocallyMuted = useIsLocallyMuted(source.userId);

  const renderMicControl = () => {
    if (local) {
      return <MicControlLocal userId={source.userId} />;
    }

    if (isLocallyMuted) {
      return <TileLocalMuteIndicator userId={source.userId} />;
    }

    return <MicControlRemote userId={source.userId} />;
  };

  const { pipWindow } = usePiPWindow();

  if (
    source.kind === 'screenshare-local' ||
    source.kind === 'screenshare-remote' ||
    source.kind === 'external'
  ) {
    return null;
  }

  return (
    <Root bottom={bottom} className={tileChromeClassName}>
      <Stack direction={bottom ? 'row-reverse' : 'row'} spacing={2}>
        <Box
          sx={[
            pipWindow
              ? {
                  pointerEvents: 'none',
                }
              : {
                  pointerEvents: 'all',
                },
          ]}
        >
          {renderMicControl()}
        </Box>
        {pipWindow ? null : <TileControls source={source} />}
      </Stack>
    </Root>
  );
});
